import type { ReactNode } from "react"
import { MapPin } from "lucide-react"

interface HeadlineHeroProps {
  eyebrow?: string
  eyebrowIcon?: ReactNode
  title: ReactNode
  accent?: string
  accentTone?: "green" | "amber"
  subtitle?: ReactNode
  align?: "center" | "left"
  // Pages that ship their own server-rendered SEO H1 above the fold can pass
  // "h2" so the in-page hero doesn't create a duplicate H1.
  headingLevel?: "h1" | "h2"
}

export function HeadlineHero({
  eyebrow,
  eyebrowIcon,
  title,
  accent,
  accentTone = "green",
  subtitle,
  align = "center",
  headingLevel = "h1",
}: HeadlineHeroProps) {
  const accentDecoration = accentTone === "amber" ? "decoration-amber-600" : "decoration-green-600"
  const accentText = accentTone === "amber" ? "text-amber-600" : "text-green-600"
  const wrapperAlign = align === "center" ? "text-center mx-auto" : "text-left"
  const Heading = headingLevel

  return (
    <div className={`max-w-2xl ${wrapperAlign} mb-6 sm:mb-8`}>
      {eyebrow && (
        <div
          className={`inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-green-700 shadow-xs ${
            align === "center" ? "" : "mb-1"
          }`}
        >
          {eyebrowIcon ?? <MapPin className="h-3.5 w-3.5" aria-hidden="true" />}
          {eyebrow}
        </div>
      )}
      <Heading className="mt-3 sm:mt-4 text-balance text-fluid-4xl font-extrabold leading-[1.2] tracking-tight text-gray-900">
        {title}
        {accent && (
          <>
            {" "}
            <span className={`${accentText} underline decoration-4 ${accentDecoration} underline-offset-[6px]`}>{accent}</span>
          </>
        )}
      </Heading>
      {subtitle && (
        <p className="mt-3 sm:mt-4 text-pretty text-fluid-base leading-relaxed text-gray-500">
          {subtitle}
        </p>
      )}
    </div>
  )
}
